import { useEffect, useState } from "react";
import { ImageOff, Loader2 } from "lucide-react";
import { getAiImageSignedUrl, useAiImageHistory, type AiImageHistoryItem } from "@/lib/hooks/useAiImages";

/** Galeria compacta do historico de imagens geradas (Modulo 3). Signed URLs
 *  sao resolvidas sob demanda no cliente (respeitando a RLS do bucket
 *  privado), nao ficam persistidas no banco. */
export function AiImageGallery(): JSX.Element {
  const { data: items = [], isLoading } = useAiImageHistory(12);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-xs text-muted-foreground">
        <Loader2 size={13} className="animate-spin" />
        Carregando histórico...
      </div>
    );
  }

  if (items.length === 0) {
    return <p className="py-4 text-xs text-muted-foreground">Nenhuma imagem gerada ainda.</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Histórico de gerações
      </p>
      <div className="grid grid-cols-4 gap-2">
        {items.map((item) => (
          <GalleryThumbnail key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

function GalleryThumbnail({ item }: { item: AiImageHistoryItem }): JSX.Element {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (item.status !== "success") {
      setFailed(true);
      return;
    }
    void getAiImageSignedUrl(item.storagePath).then((url) => {
      if (cancelled) return;
      if (url) setSignedUrl(url);
      else setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [item.storagePath, item.status]);

  return (
    <a
      href={signedUrl ?? undefined}
      target="_blank"
      rel="noreferrer"
      title={item.sceneLabel}
      className="flex aspect-square items-center justify-center overflow-hidden rounded-md border border-border bg-[hsl(var(--surface-raised)/0.4)]"
    >
      {signedUrl ? (
        <img src={signedUrl} alt={item.sceneLabel} className="h-full w-full object-cover" />
      ) : failed ? (
        <ImageOff size={16} className="text-muted-foreground" />
      ) : (
        <Loader2 size={14} className="animate-spin text-muted-foreground" />
      )}
    </a>
  );
}
